import React, { FC, ReactNode } from "react";
import { Platform, StyleProp, Text, TextProps, TextStyle } from "react-native";
import variables from "../../styles/variables";

/**
 * RText component for rendering text with customisable styles.
 * Supports dynamic styles based on hover and press states.
 *
 * @component
 * @param {RTextProps} props - The properties for the RText component (they are self-explanatory).
 * @returns {JSX.Element} The rendered text component.
 */

const { colors: fc, weight: fw, size: fs, family: ff } = variables.font;

export type RTextFont = keyof typeof ff;
export type RTextColors = keyof typeof fc;
export type RTextWeight = keyof typeof fw;
export type RTextSize = keyof typeof fs;
export type RIconSize = keyof typeof variables.sizes;

type DynamicTextStyle =
  | "changeBackgroundOpacity"
  | "underline"
  | "changeTextColor"
  | "none";

export type RTextProps = TextProps & {
  children: string | JSX.Element | JSX.Element[] | ReactNode;
  family?: RTextFont;
  color?: RTextColors | string;
  weight?: RTextWeight;
  size?: RTextSize;
  style?: StyleProp<TextStyle>;
  uppercase?: boolean;
  capitalize?: boolean;
  italic?: boolean;
  dynamicTextStyle?: {
    onHover?: DynamicTextStyle;
    onPressed?: DynamicTextStyle;
  };
  onPress?: () => void;
  lowercase?: boolean;
  lineHeight?: number;
  dataSet?: object;
};

export const textStyles: { [key: string]: TextStyle } = {
  changeBackgroundOpacity: {
    backgroundColor: "rgba(0,0,0,0.8)",
  },
  underline: {
    textDecorationLine: "underline",
  },
  changeTextColor: {
    opacity: 0.7,
  },
  none: {},
};

const RText: FC<RTextProps> = ({
  family = "primary",
  color,
  weight = "regular",
  size = "sm",
  style,
  uppercase,
  capitalize,
  italic,
  dynamicTextStyle,
  onPress,
  lowercase,
  lineHeight,
  dataSet,
  ...rest
}) => {
  const [hovered, setHovered] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);

  const baseStyles: TextStyle[] = [
    family && { fontFamily: `${ff[family]}_${fw[weight]}` },
    { color: (color && (fc[color] || color)) || "#1d1c39" },
    size && { fontSize: fs[size] },
    uppercase && { textTransform: "uppercase" },
    capitalize && { textTransform: "capitalize" },
    italic && { fontStyle: "italic" },
    lowercase && { textTransform: "lowercase" },
    lineHeight && { lineHeight },
    style,
  ];

  if (Platform.OS == "web") {
    return (
      <Text
        //@ts-ignore
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={[
          ...baseStyles,
          hovered &&
            dynamicTextStyle?.onHover &&
            textStyles[dynamicTextStyle.onHover],
          pressed &&
            dynamicTextStyle?.onPressed &&
            textStyles[dynamicTextStyle.onPressed],
        ]}
        dataSet={dataSet}
        {...rest}
      />
    );
  }
  if (onPress) {
    return (
      <Text
        onPress={() => onPress?.()}
        onPressIn={() => setPressed(true)}
        onPressOut={() => setPressed(false)}
        style={[
          ...baseStyles,
          hovered &&
            dynamicTextStyle?.onHover &&
            textStyles[dynamicTextStyle.onHover],
          pressed &&
            dynamicTextStyle?.onPressed &&
            textStyles[dynamicTextStyle.onPressed],
        ]}
        suppressHighlighting //turn off grey background on iOS
        {...rest}
      />
    );
  } else
    return (
      <Text
        style={baseStyles}
        suppressHighlighting //turn off grey background on iOS
        {...rest}
      />
    );
};

export default RText;
